import { Alert, Box, Button, Card, CardContent, Stack, TextField, Typography } from '@mui/material';
import { useState } from 'react';
import type { JSX } from 'react';
import { useNavigate } from 'react-router-dom';
import { ApiError } from '../api/client.js';
import { useAuth } from '../auth/AuthProvider.js';

/**
 * Login do painel. MFA é solicitado quando o servidor exige (MFA_REQUIRED) —
 * o app não decide sozinho se o segundo fator vale.
 */
export function LoginPage(): JSX.Element {
  const { signIn, session } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [totp, setTotp] = useState('');
  const [needsTotp, setNeedsTotp] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  if (session) {
    navigate('/overview', { replace: true });
  }

  async function handleSubmit(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await signIn(email, password, totp || undefined);
      navigate('/overview', { replace: true });
    } catch (caught) {
      if (caught instanceof ApiError && caught.code === 'MFA_REQUIRED') {
        setNeedsTotp(true);
        setError('Informe o código do aplicativo autenticador.');
      } else if (caught instanceof ApiError) {
        setError(caught.message);
      } else {
        setError('Falha inesperada ao entrar. Tente novamente.');
      }
      // Entrada preservada após erro: os campos não são limpos.
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Card>
      <CardContent>
        <Typography variant="h1" gutterBottom>
          Entrar na operação
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Área restrita à equipe autorizada. Todo acesso a dados de famílias é registrado
          em auditoria.
        </Typography>
        <Box component="form" onSubmit={handleSubmit} noValidate>
          <Stack spacing={2}>
            {error ? <Alert severity="error">{error}</Alert> : null}
            <TextField
              label="E-mail"
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              required
              autoComplete="username"
              fullWidth
            />
            <TextField
              label="Senha"
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              required
              autoComplete="current-password"
              fullWidth
            />
            {needsTotp ? (
              <TextField
                label="Código de verificação"
                value={totp}
                onChange={(event) => setTotp(event.target.value)}
                inputProps={{ inputMode: 'numeric', pattern: '\\d{6}' }}
                required
                fullWidth
              />
            ) : null}
            <Button type="submit" variant="contained" disabled={submitting}>
              {submitting ? 'Entrando...' : 'Entrar'}
            </Button>
          </Stack>
        </Box>
      </CardContent>
    </Card>
  );
}